import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { NexoLogo } from '@/components/NexoLogo';
import { authService } from '@/services/auth/authService';
import { tenantResolver } from '@/services/tenantResolver';
import { tenantSlugFromHostname, isHostLoginHostname, isTenantBoundHostname } from '@/services/tenantDomain';
import { loadTenantEntryConfig, subdomainEntryEnabled } from '@/services/tenantEntryConfig';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface SmartTenantLoginProps {
  onError?: (error: Error) => void;
}

/**
 * Login entry (D2/D13b/D18): the React password form is retired — all interactive
 * logins go through authorization code + PKCE on the ABP login page (Nexo-branded, D10).
 *
 * Under D18 this screen has three shapes, decided by the HOSTNAME rather than by
 * anything the user remembers:
 *
 *  1. **A tenant subdomain** (`tenant1.nexo.com`) — the tenant is already decided.
 *     The hostname ALWAYS wins over a stored tenant from a previous session, and a
 *     mismatch clears the previous tenant's local state before the new flow starts.
 *     No picker is shown and no `__tenant` is sent: the auth host derives the tenant
 *     from the request's redirect_uri, and a hint that disagreed would be rejected.
 *  2. **The reserved host-login subdomain** (`admin.nexo.com`) — a host-level session,
 *     equivalent to today's empty-tenant-field path.
 *  3. **The neutral entry point** (apex / `www` / an unmapped subdomain / `localhost`)
 *     — a minimal "choose your organization" field that resolves against
 *     `TenantDomain.Slug` and then NAVIGATES (full page load) to the resolved
 *     hostname. Submitting never starts `/connect/authorize` from here, so exactly one
 *     code path ever calls it: the tenant's own subdomain.
 */
/**
 * The installation answers "do we use tenant subdomains?" before this screen picks a
 * shape. Deciding on the hostname alone is the guess that made a custom domain
 * unusable: `nexo-test.example.com` looks exactly like `tenant1.nexo.com`, so the
 * screen skipped the picker, cleared the stored tenant and started a login round that
 * carried no tenant at all.
 */
export function SmartTenantLogin(props: SmartTenantLoginProps) {
  const { t } = useTranslation();
  const [ready, setReady] = useState(subdomainEntryEnabled() !== null);

  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    void loadTenantEntryConfig().then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [ready]);

  if (!ready) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">{t('login.redirecting', 'Redirecting to sign-in…')}</Typography>
      </Box>
    );
  }

  return <SmartTenantLoginShape {...props} />;
}

function SmartTenantLoginShape({ onError }: SmartTenantLoginProps) {
  const { t } = useTranslation();
  const { beginLogin } = useAuth();

  const search = new URLSearchParams(window.location.search);
  const forcePick = search.has('pick');
  /**
   * The automatic `prompt=none` round is spent and came back `login_required`
   * (D18 item 36). Exactly one automatic attempt happens per load, so this screen must
   * NOT start another authorize round by itself — it surfaces an explicit action.
   * Without this, a tenant subdomain reached while another tenant's auth-host session
   * is still live could complete an interactive round off the live Entra session and
   * switch the user's tenant with no act on their part.
   */
  const interactionRequired = search.get('interaction') === 'required';

  // The hostname is authoritative. Read once per mount — it cannot change without a
  // full navigation, which remounts everything anyway.
  // Shape AND configuration: a hostname can only decide the tenant in an installation
  // that routes login through tenant subdomains at all. Without that second fact every
  // custom domain reads as a tenant subdomain (see isTenantBoundHostname).
  const hostnameDecidesTenant = isTenantBoundHostname(
    window.location.hostname,
    subdomainEntryEnabled(),
  );
  const hostnameSlug = hostnameDecidesTenant ? tenantSlugFromHostname(window.location.hostname) : null;
  const onHostLoginDomain = hostnameDecidesTenant && isHostLoginHostname(window.location.hostname);

  // `?pick=1` is the tenant-switch entry (and the post-logout landing). On a
  // tenant-bound domain it must NOT re-pick in place: the picker below resolves the
  // target and NAVIGATES to that tenant's own domain, so exactly one code path ever
  // reaches /connect/authorize — the tenant's own subdomain (item 28).
  const onTenantDomain = hostnameSlug !== null && !onHostLoginDomain && !forcePick;

  const knownTenantId = authService.getTenantId();
  const knownTenantName = authService.getTenantName() || '';

  // On a tenant-bound or host-login domain there is nothing to ask: go straight to
  // the ABP login page. `?pick=1` (e.g. after logout) is the only thing that forces
  // the picker, and only where a picker makes sense at all.
  const canAutoRedirect =
    !interactionRequired &&
    (onTenantDomain || onHostLoginDomain || (!forcePick && (!!knownTenantId || !!knownTenantName)));

  const [mode, setMode] = useState<'redirecting' | 'form'>(canAutoRedirect ? 'redirecting' : 'form');
  const [tenantName, setTenantName] = useState(onTenantDomain ? hostnameSlug! : knownTenantName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoStarted = useRef(false);

  /**
   * Start the authorization round for the domain we are already on. The tenant is
   * whatever the hostname says; the SPA sends no `__tenant` at all, because under D18
   * it is a non-authoritative hint that can only agree or be rejected — it can never
   * repair a mismatch.
   */
  const startLoginForThisDomain = async (): Promise<void> => {
    if (onTenantDomain) {
      // The hostname-derived tenant takes precedence over anything stored. A mismatch
      // clears the previous tenant's local state (in-memory D13b tokens are already
      // per-tab and unaffected, but tenant-scoped local data must not survive).
      await authService.adoptHostnameTenant(hostnameSlug!);
    } else if (onHostLoginDomain) {
      await authService.adoptHostLogin();
    }

    // Redirects via /connect/authorize; control does not return.
    await beginLogin(undefined, '/');
  };

  /** The neutral entry point: resolve, then NAVIGATE — never authorize from here. */
  const submitOrganizationName = async (name: string): Promise<void> => {
    const result = await tenantResolver.resolveTenantDomain(name);

    if (!result.found) {
      // Active, inactive and unknown are answered identically by the server; the user
      // sees one message for all three.
      throw new Error(
        t('login.organizationNotFound', 'That organization could not be found.') as string,
      );
    }

    if (result.origin) {
      // Built from the RESOLVED row, never concatenated from what the user typed.
      window.location.assign(`${result.origin}/login`);
      return;
    }

    // Pre-D18 environments have no tenant subdomains to navigate to: the server
    // answered with the tenant id and this behaves exactly as it did before.
    authService.setTenantName(name.trim().toLowerCase());
    await beginLogin(result.tenantId, '/');
  };

  useEffect(() => {
    if (mode !== 'redirecting' || autoStarted.current) return;
    autoStarted.current = true;
    void (async () => {
      try {
        if (onTenantDomain || onHostLoginDomain) {
          await startLoginForThisDomain();
          return;
        }

        if (knownTenantName) {
          // Neutral domain with a remembered organization NAME: resolve it and
          // navigate (or, pre-D18, log in with the id the server returns).
          await submitOrganizationName(knownTenantName);
          return;
        }

        // Remembered id but no name — a pre-D18 session. Resolving needs a name, so
        // going through the lookup here would bounce a user who has a perfectly good
        // tenant straight to the picker. Use the id, exactly as before D18.
        await beginLogin(knownTenantId ?? undefined, '/');
      } catch (err) {
        // The remembered organization no longer resolves — fall back to the picker.
        setError(err instanceof Error ? err.message : 'Sign-in could not be started');
        setMode('form');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleSubmit = async () => {
    // The neutral picker requires an organization: an empty name is not a host-login
    // path here (host administration is reached through the admin app, not this screen).
    if (tenantName.trim().length === 0) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await submitOrganizationName(tenantName);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in could not be started';
      setError(message);
      onError?.(err instanceof Error ? err : new Error(message));
      setIsSubmitting(false);
    }
  };

  // A tenant-bound or host-login domain has nothing to ask — but after a spent silent
  // round it still needs an explicit act. One button, no organization field: the
  // hostname-derived tenant is retained, never re-picked.
  if (onTenantDomain || onHostLoginDomain) {
    if (mode === 'redirecting') {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography color="text.secondary">{t('login.redirecting', 'Redirecting to sign-in…')}</Typography>
        </Box>
      );
    }

    return (
      <Container maxWidth="sm">
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <NexoLogo variant="light" height={48} />
              </Box>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                {onHostLoginDomain
                  ? t('login.hostAdministration', 'Host administration')
                  : hostnameSlug}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                onClick={() => {
                  setIsSubmitting(true);
                  setError(null);
                  void startLoginForThisDomain().catch((err) => {
                    const message = err instanceof Error ? err.message : 'Sign-in could not be started';
                    setError(message);
                    onError?.(err instanceof Error ? err : new Error(message));
                    setIsSubmitting(false);
                  });
                }}
              >
                {isSubmitting ? t('login.signingIn') : t('login.continueToSignIn', 'Continue to sign-in')}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  if (mode === 'redirecting') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">{t('login.redirecting', 'Redirecting to sign-in…')}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <NexoLogo height={40} />
            </Box>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              {t('login.chooseTenantToContinue', 'Choose your organization to continue')}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              label={t('login.tenantName', 'Organization name')}
              fullWidth
              margin="normal"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              disabled={isSubmitting}
              autoFocus
              placeholder="alfa"
              helperText={t('login.organizationRequired', 'Enter your organization name to continue')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSubmit();
              }}
            />

            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={isSubmitting || tenantName.trim().length === 0}
              sx={{ mt: 2 }}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              onClick={() => void handleSubmit()}
            >
              {isSubmitting ? t('login.signingIn') : t('login.continueToSignIn', 'Continue to sign-in')}
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
